// Cloud Library API client for Excalidraw

export interface DesignVersion {
  version: number;
  timestamp: string;
  size: number;
}

export interface Design {
  designId: string;
  title: string;
  versions: DesignVersion[];
  latestVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListResponse {
  designs: Design[];
  totalDesigns: number;
  totalVersions: number;
}

export interface SaveResponse {
  success: boolean;
  designId: string;
  version: number;
  timestamp: string;
}

export interface LoadResponse {
  designId: string;
  version: number;
  data: {
    elements: any[];
    appState: any;
    files?: any;
  };
}

const API_BASE = "/api";

// Check if we're running locally OR if API fails (fallback mode)
const isLocalDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Use localStorage as fallback until Azure Blob Storage is configured
// Set this to false once you have AZURE_STORAGE_CONNECTION_STRING configured
const USE_LOCAL_STORAGE_FALLBACK = false;

// Local storage mock for development/fallback
const LOCAL_STORAGE_KEY = "excalidraw-cloud-library";

function getLocalDesigns(): Design[] {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveLocalDesigns(designs: Design[]): void {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(designs));
}

function getLocalDesignData(
  designId: string,
  version?: number,
): { elements: any[]; appState: any; files?: any } | null {
  try {
    const key = version
      ? `${LOCAL_STORAGE_KEY}-data-${designId}-v${version}`
      : `${LOCAL_STORAGE_KEY}-data-${designId}-latest`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function saveLocalDesignData(
  designId: string,
  version: number,
  data: { elements: any[]; appState: any; files?: any },
): void {
  localStorage.setItem(
    `${LOCAL_STORAGE_KEY}-data-${designId}-v${version}`,
    JSON.stringify(data),
  );
  localStorage.setItem(
    `${LOCAL_STORAGE_KEY}-data-${designId}-latest`,
    JSON.stringify(data),
  );
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function listDesigns(): Promise<ListResponse> {
  // Use local storage for development or fallback mode
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    const designs = getLocalDesigns();
    return {
      designs,
      totalDesigns: designs.length,
      totalVersions: designs.reduce((sum, d) => sum + d.versions.length, 0),
    };
  }

  const response = await fetch(`${API_BASE}/list`, {
    method: "GET",
    credentials: "include",
  });
  return handleResponse<ListResponse>(response);
}

export async function saveDesign(
  data: { elements: readonly any[]; appState: any; files?: any },
  designId?: string,
  title?: string,
): Promise<SaveResponse> {
  // Use local storage for development or fallback mode
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    const designs = getLocalDesigns();
    const now = new Date().toISOString();
    const id = designId || `local-${Date.now()}`;
    const existingIndex = designs.findIndex((d) => d.designId === id);

    if (existingIndex >= 0) {
      const existing = designs[existingIndex];
      const newVersion = existing.latestVersion + 1;
      existing.versions.push({
        version: newVersion,
        timestamp: now,
        size: JSON.stringify(data).length,
      });
      existing.latestVersion = newVersion;
      existing.updatedAt = now;
      if (title) {
        existing.title = title;
      }
      saveLocalDesignData(id, newVersion, data as any);
      saveLocalDesigns(designs);
      return { success: true, designId: id, version: newVersion, timestamp: now };
    } else {
      const newDesign: Design = {
        designId: id,
        title: title || `Design ${designs.length + 1}`,
        versions: [{ version: 1, timestamp: now, size: JSON.stringify(data).length }],
        latestVersion: 1,
        createdAt: now,
        updatedAt: now,
      };
      designs.push(newDesign);
      saveLocalDesignData(id, 1, data as any);
      saveLocalDesigns(designs);
      return { success: true, designId: id, version: 1, timestamp: now };
    }
  }

  const response = await fetch(`${API_BASE}/save`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      designId,
      title,
      data: JSON.stringify(data),
    }),
  });
  return handleResponse<SaveResponse>(response);
}

export async function loadDesign(
  designId: string,
  version?: number,
): Promise<LoadResponse> {
  // Use local storage for development or fallback mode
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    const designs = getLocalDesigns();
    const design = designs.find((d) => d.designId === designId);
    if (!design) {
      throw new Error("Design not found");
    }
    const targetVersion = version || design.latestVersion;
    const data = getLocalDesignData(designId, targetVersion);
    if (!data) {
      throw new Error("Design data not found");
    }
    return { designId, version: targetVersion, data };
  }

  const versionPath = version ? `/${version}` : "/latest";
  const response = await fetch(`${API_BASE}/load/${designId}${versionPath}`, {
    method: "GET",
    credentials: "include",
  });
  return handleResponse<LoadResponse>(response);
}

export async function deleteDesign(
  designId: string,
  version?: number,
  deleteAll?: boolean,
): Promise<void> {
  // Use local storage for development or fallback mode
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    const designs = getLocalDesigns();
    const designIndex = designs.findIndex((d) => d.designId === designId);
    if (designIndex === -1) {
      return;
    }

    if (deleteAll || designs[designIndex].versions.length <= 1) {
      // Remove entire design
      designs.splice(designIndex, 1);
    } else if (version) {
      // Remove specific version
      const design = designs[designIndex];
      design.versions = design.versions.filter((v) => v.version !== version);
      if (design.versions.length > 0) {
        design.latestVersion = Math.max(...design.versions.map((v) => v.version));
      }
    }
    saveLocalDesigns(designs);
    return;
  }

  const response = await fetch(`${API_BASE}/manage`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      designId,
      version,
      deleteAll,
    }),
  });
  await handleResponse<{ success: boolean }>(response);
}

export async function renameDesign(
  designId: string,
  title: string,
): Promise<void> {
  // Use local storage for development or fallback mode
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    const designs = getLocalDesigns();
    const design = designs.find((d) => d.designId === designId);
    if (design) {
      design.title = title;
      design.updatedAt = new Date().toISOString();
      saveLocalDesigns(designs);
    }
    return;
  }

  const response = await fetch(`${API_BASE}/manage`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      designId,
      title,
    }),
  });
  await handleResponse<{ success: boolean }>(response);
}

// Check if user is authenticated
export async function checkAuth(): Promise<boolean> {
  // For local development or fallback mode, skip auth check
  if (isLocalDev || USE_LOCAL_STORAGE_FALLBACK) {
    return true;
  }
  try {
    const response = await fetch("/.auth/me", { credentials: "include" });
    const data = await response.json();
    return data.clientPrincipal !== null;
  } catch {
    return false;
  }
}

// Get current user info
export async function getCurrentUser(): Promise<{
  userId: string;
  userDetails: string;
} | null> {
  try {
    const response = await fetch("/.auth/me", { credentials: "include" });
    const data = await response.json();
    return data.clientPrincipal;
  } catch {
    return null;
  }
}
